// ---------------------------------------------------------------------------
// Tenant Admin module — Projects list page
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { api, getDebugHeaders } from '../lib/api';
const apiBase = import.meta.env.VITE_API_BASE_URL || '';
import { useProjectContext } from '../lib/project-context';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Create project form
// ---------------------------------------------------------------------------

function CreateProjectForm({
  tenantId,
  onCreated,
}: {
  tenantId: string;
  onCreated: () => void;
}) {
  const { t } = useTranslation('admin');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const idempotencyKey = `proj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const response = await fetch(`${apiBase}/v1/admin/tenants/${tenantId}/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          ...getDebugHeaders(),
        },
        body: JSON.stringify({ name, slug }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          (body as { error?: { message?: string } })?.error?.message ?? `HTTP ${response.status}`,
        );
      }

      setName('');
      setSlug('');
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('projects.errors.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-surface-raised rounded-sm shadow p-6 mb-6">
      <h3 className="text-lg font-semibold text-ink mb-4">{t('projects.form.title')}</h3>

      {error && (
        <div className="mb-4 p-3 bg-danger-tint border border-danger/30 rounded text-danger text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="project-name" className="block text-sm font-medium text-ink mb-1">
            {t('projects.form.name')}
          </label>
          <input
            id="project-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('projects.form.namePlaceholder')}
            required
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="project-slug" className="block text-sm font-medium text-ink mb-1">
            {t('projects.form.slug')}
          </label>
          <input
            id="project-slug"
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder={t('projects.form.slugPlaceholder')}
            required
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? t('projects.actions.creating') : t('projects.actions.create')}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function ProjectsPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const columns = useMemo<Column<admin.ProjectSummary>[]>(
    () => [
      {
        key: 'name',
        header: t('projects.columns.name'),
        render: (p) => (
          <span className="text-sm font-medium text-ink">{p.name}</span>
        ),
      },
      {
        key: 'slug',
        header: t('projects.columns.slug'),
        render: (p) => (
          <span className="text-sm text-ink-muted font-mono">{p.slug}</span>
        ),
      },
      {
        key: 'status',
        header: t('projects.columns.status'),
        render: (p) => <StatusBadge value={p.status} />,
      },
      {
        key: 'jurisdictionId',
        header: t('projects.columns.jurisdiction'),
        render: (p) => (
          <span className="text-sm text-ink-muted">{p.jurisdictionId}</span>
        ),
      },
      {
        key: 'createdAt',
        header: t('projects.columns.created'),
        render: (p) => (
          <span className="text-sm text-ink-muted tabular-nums font-mono">
            {f.date(p.createdAt, { dateStyle: 'medium' })}
          </span>
        ),
      },
    ],
    [f, t],
  );
  const { tenantId } = useProjectContext();
  const [projects, setProjects] = useState<admin.ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const loadProjects = useCallback(() => {
    if (!tenantId) {
      setProjects([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    api.admin
      .listProjects(tenantId)
      .then((res) => setProjects(res.data))
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message: unknown }).message)
              : t('projects.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [tenantId, t]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  if (!tenantId) {
    return (
      <div className="p-6">
        <h1 className="font-display text-3xl text-ink mb-4">{t('projects.headings.list')}</h1>
        <div className="bg-primary-tint border border-primary/30 rounded-sm p-4 text-primary">
          <p className="text-sm">
            {t('projects.messages.selectProject')}
          </p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadProjects} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('projects.headings.list')}</h1>
        <button
          type="button"
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
        >
          {showCreate ? t('projects.actions.cancel') : t('projects.actions.create')}
        </button>
      </div>

      {showCreate && (
        <CreateProjectForm
          tenantId={tenantId}
          onCreated={() => {
            setShowCreate(false);
            loadProjects();
          }}
        />
      )}

      <DataTable
        columns={columns}
        data={projects}
        rowKey={(p) => p.id}
        emptyMessage={t('projects.empty')}
        ariaLabel={t('projects.ariaLabel')}
      />
    </div>
  );
}
